/**
 * The header's sign-in control: a Sign in button before authentication, the account menu
 * after.
 *
 * Ported from the V2 portal's `components/AuthButton.tsx`. The Graph photo fetch is not
 * carried over — the avatar renders initials from the ID token claims, so the control
 * needs no Graph consent.
 */
import type { Ref } from 'react';
import {
  Avatar,
  Button,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Person24Regular, SignOut24Regular } from '@fluentui/react-icons';
import { useAuth } from '../../hooks/useAuth';

const useStyles = makeStyles({
  trigger: {
    minWidth: 0,
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    ...shorthands.padding(0),
  },
  identity: {
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    display: 'flex',
    flexDirection: 'column',
  },
  name: { fontWeight: tokens.fontWeightSemibold },
  mail: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  itemIcon: { display: 'inline-flex', marginRight: tokens.spacingHorizontalXS },
});

export function AuthButton({ onViewProfile, accountButtonRef }: {
  onViewProfile?: () => void;
  accountButtonRef?: Ref<HTMLButtonElement>;
}) {
  const styles = useStyles();
  const { isAuthenticated, isLoading, account, userProfile, login, logout } = useAuth();

  if (isLoading) {
    return (
      <Button appearance="outline" disabled>
        <Spinner size="tiny" labelPosition="after" label="Signing in…" />
      </Button>
    );
  }

  if (!isAuthenticated) {
    return (
      <Button appearance="primary" onClick={() => void login()}>
        Sign in
      </Button>
    );
  }

  const displayName = userProfile?.displayName || account?.username || 'User';
  const mail = userProfile?.mail || account?.username || '';

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          ref={accountButtonRef}
          appearance="subtle"
          className={styles.trigger}
          aria-label={`Account: ${displayName}`}
        >
          <Avatar name={displayName} />
        </Button>
      </MenuTrigger>
      <MenuPopover>
        <div className={styles.identity}>
          <span className={styles.name}>{displayName}</span>
          <span className={styles.mail}>{mail}</span>
        </div>
        <MenuDivider />
        <MenuList>
          {onViewProfile && (
            <MenuItem icon={<Person24Regular />} onClick={onViewProfile}>
              View profile
            </MenuItem>
          )}
          <MenuItem onClick={() => void logout()}>
            <span className={styles.itemIcon}>
              <SignOut24Regular />
            </span>
            Sign out
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
